/**
 * Authenticated App Layout
 * Persistent collapsible sidebar with role-based navigation, a glass topbar
 * (page title, global search, theme toggle, user menu), and toast host.
 */

import { useEffect, useRef, useState } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { buildNavigation, flattenNavigation, findActiveNavigationItem } from '@/config/navigation'
import {
  getInitialTheme,
  setTheme,
  type Theme,
  getInitialAccent,
  setAccent,
  ACCENTS,
  type Accent,
} from '@/utils/theme'
import ToastContainer from '@/components/ui/ToastContainer'
import TenantSupportNotice from '@/components/TenantSupportNotice'
import {
  User,
  Users,
  FileText,
  BarChart3,
  Home,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Search,
  Settings,
  Briefcase,
  Moon,
  Sun,
  LogOut,
  Scale,
  Palette,
  Menu,
  Check,
} from 'lucide-react'

const iconMap: Record<string, typeof Home> = {
  dashboard: Home,
  customers: Users,
  clients: Briefcase,
  cases: FileText,
  'return-cases': FileText,
  search: Search,
  users: User,
  settings: Settings,
  analytics: BarChart3,
  'settings-group': Settings,
}

/** Human page title from the current pathname (first matching nav label, with fallbacks). */
function getPageTitle(pathname: string, navItems: { path: string; label: string }[]): string {
  const match = findActiveNavigationItem(pathname, navItems)
  if (match) return match.label
  if (pathname.startsWith('/profile')) return 'Profile'
  if (pathname.startsWith('/settings')) return 'Settings'
  if (pathname.startsWith('/sessions')) return 'Active Sessions'
  if (pathname.startsWith('/audit')) return 'Audit Log'
  if (pathname.startsWith('/search')) return 'Search'
  return 'TaxHive'
}

function initials(name?: string): string {
  if (!name) return 'U'
  const parts = name.trim().split(/\s+/)
  return (parts[0]?.[0] ?? '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')
}

export default function AppLayout() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout } = useAuth()
  const [mobileNavigationOpen, setMobileNavigationOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [theme, setThemeState] = useState<Theme>(getInitialTheme())
  const [accent, setAccentState] = useState<Accent>(getInitialAccent())
  const [menuOpen, setMenuOpen] = useState(false)
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())
  const [accentOpen, setAccentOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const accentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setMobileNavigationOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (!mobileNavigationOpen) return
    const close = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMobileNavigationOpen(false)
    }
    document.addEventListener('keydown', close)
    return () => document.removeEventListener('keydown', close)
  }, [mobileNavigationOpen])

  // Close the user menu on outside click / Escape.
  useEffect(() => {
    if (!menuOpen) return
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false)
    }
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMenuOpen(false)
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [menuOpen])

  // Close the accent menu on outside click / Escape.
  useEffect(() => {
    if (!accentOpen) return
    const onClick = (e: MouseEvent) => {
      if (accentRef.current && !accentRef.current.contains(e.target as Node)) setAccentOpen(false)
    }
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setAccentOpen(false)
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [accentOpen])

  const pickAccent = (a: Accent) => {
    setAccent(a)
    setAccentState(a)
    setAccentOpen(false)
  }

  if (!user) return null

  const navItems = buildNavigation(user)
  const flatItems = flattenNavigation(navItems)
  const activeNavItem = findActiveNavigationItem(location.pathname, flatItems)
  const pageTitle = getPageTitle(location.pathname, flatItems)

  // Auto-expand the parent group when a child is active
  const activeParentId = navItems.find(
    item =>
      item.children &&
      item.children.some(
        child => location.pathname === child.path || location.pathname.startsWith(`${child.path}/`)
      )
  )?.id

  const toggleGroup = (groupId: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev)
      if (next.has(groupId)) next.delete(groupId)
      else next.add(groupId)
      return next
    })
  }

  const toggleTheme = () => {
    const next: Theme = theme === 'dark' ? 'light' : 'dark'
    setTheme(next)
    setThemeState(next)
  }

  const roleLabel = String(user.role).replace(/_/g, ' ')

  return (
    <div className="flex h-screen bg-surface-light dark:bg-surface-dark">
      {mobileNavigationOpen && (
        <button className="taxhive-mobile-navigation-backdrop" aria-label="Close navigation"
          onClick={() => setMobileNavigationOpen(false)} />
      )}
      {/* Sidebar */}
      <aside
        id="authenticated-navigation"
        data-mobile-open={mobileNavigationOpen}
        className={`taxhive-auth-sidebar flex flex-col bg-surface-light dark:bg-surface-dark border-r border-surface-overlay transition-all duration-300 z-20 relative ${
          sidebarCollapsed ? 'w-20' : 'w-64'
        }`}
      >
        {/* Brand / header */}
        <div
          className={`h-16 border-b border-surface-overlay flex items-center ${
            sidebarCollapsed ? 'justify-center px-2' : 'justify-between px-4'
          }`}
        >
          {!sidebarCollapsed ? (
            <button
              onClick={() =>
                navigate(user.authScope === 'client_vault' ? '/client-vault' : '/dashboard')
              }
              className="flex items-center gap-3 rounded-lg p-1 -m-1"
            >
              <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white shadow-glow flex-shrink-0">
                <Scale className="w-5 h-5" />
              </span>
              <span className="text-left">
                <span className="block text-base font-bold text-text-primary leading-tight tracking-tight">
                  TaxHive
                </span>
                <span className="block text-[11px] text-text-tertiary leading-tight">
                  Case Intelligence
                </span>
              </span>
            </button>
          ) : (
            <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white shadow-glow">
              <Scale className="w-5 h-5" />
            </span>
          )}
          {!sidebarCollapsed && (
            <button
              onClick={() => setSidebarCollapsed(true)}
              className="p-1.5 hover:bg-surface-overlay rounded-lg transition text-text-secondary"
              title="Collapse sidebar"
              aria-label="Collapse sidebar"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
          )}
        </div>

        {sidebarCollapsed && (
          <button
            onClick={() => setSidebarCollapsed(false)}
            className="mx-auto mt-3 p-1.5 hover:bg-surface-overlay rounded-lg transition text-text-secondary"
            title="Expand sidebar"
            aria-label="Expand sidebar"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        )}

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-3 space-y-1">
          {navItems.map(item => {
            const Icon = iconMap[item.id] || FileText

            // Submenu group
            if (item.children && item.children.length > 0) {
              const isGroupExpanded = expandedGroups.has(item.id) || activeParentId === item.id
              const hasActiveChild = item.children.some(child => activeNavItem?.path === child.path)

              return (
                <div key={item.id}>
                  <button
                    onClick={() =>
                      sidebarCollapsed ? navigate(item.children![0].path) : toggleGroup(item.id)
                    }
                    title={sidebarCollapsed ? item.label : undefined}
                    className={`relative w-full text-left px-3 py-2.5 rounded-lg flex items-center ${
                      sidebarCollapsed ? 'justify-center' : 'gap-3'
                    } transition-colors ${
                      hasActiveChild
                        ? 'text-primary-700 dark:text-primary-400 font-semibold'
                        : 'text-text-secondary hover:bg-surface-overlay hover:text-text-primary font-medium'
                    }`}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    {!sidebarCollapsed && (
                      <>
                        <span className="truncate flex-1">{item.label}</span>
                        <ChevronDown
                          className={`w-4 h-4 flex-shrink-0 transition-transform duration-200 ${
                            isGroupExpanded ? 'rotate-180' : ''
                          }`}
                        />
                      </>
                    )}
                  </button>
                  {isGroupExpanded && !sidebarCollapsed && (
                    <div className="ml-4 mt-0.5 space-y-0.5 border-l-2 border-surface-overlay pl-3">
                      {item.children.map(child => {
                        const ChildIcon = iconMap[child.id] || FileText
                        const isChildActive = activeNavItem?.path === child.path
                        return (
                          <button
                            key={child.id}
                            onClick={() => navigate(child.path)}
                            aria-current={isChildActive ? 'page' : undefined}
                            className={`relative w-full text-left px-3 py-2 rounded-lg flex items-center gap-3 text-sm transition-colors ${
                              isChildActive
                                ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400 font-semibold'
                                : 'text-text-secondary hover:bg-surface-overlay hover:text-text-primary font-medium'
                            }`}
                          >
                            {isChildActive && (
                              <span className="absolute left-[-13px] top-1/2 -translate-y-1/2 h-4 w-0.5 rounded-r-full bg-primary-600" />
                            )}
                            <ChildIcon className="w-4 h-4 flex-shrink-0" />
                            <span className="truncate">{child.label}</span>
                          </button>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            }

            // Regular (non-group) item
            const isActive = activeNavItem?.path === item.path

            return (
              <button
                key={item.id}
                onClick={() => navigate(item.path)}
                title={sidebarCollapsed ? item.label : undefined}
                aria-current={isActive ? 'page' : undefined}
                className={`relative w-full text-left px-3 py-2.5 rounded-lg flex items-center ${
                  sidebarCollapsed ? 'justify-center' : 'gap-3'
                } transition-colors ${
                  isActive
                    ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400 font-semibold'
                    : 'text-text-secondary hover:bg-surface-overlay hover:text-text-primary font-medium'
                }`}
              >
                {isActive && !sidebarCollapsed && (
                  <span className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-1 rounded-r-full bg-primary-600" />
                )}
                <Icon className="w-5 h-5 flex-shrink-0" />
                {!sidebarCollapsed && <span className="truncate">{item.label}</span>}
              </button>
            )
          })}
        </nav>

        {/* Footer — user chip */}
        <div className="p-3 border-t border-surface-overlay">
          <button
            onClick={() =>
              navigate(user.authScope === 'client_vault' ? '/client-vault' : '/profile')
            }
            title={sidebarCollapsed ? 'Profile' : undefined}
            className={`w-full rounded-lg flex items-center transition-colors text-text-secondary hover:bg-surface-overlay ${
              sidebarCollapsed ? 'justify-center p-2' : 'gap-3 px-3 py-2.5'
            }`}
          >
            <span className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 flex items-center justify-center text-xs font-semibold uppercase flex-shrink-0">
              {initials(user.fullName)}
            </span>
            {!sidebarCollapsed && (
              <span className="min-w-0 text-left">
                <span className="block text-sm font-medium text-text-primary truncate">
                  {user.fullName}
                </span>
                <span className="block text-[11px] text-text-tertiary capitalize truncate">
                  {roleLabel}
                </span>
              </span>
            )}
          </button>
        </div>
      </aside>

      {/* Main column */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Glass topbar */}
        <header className="glass-surface h-16 border-b border-surface-overlay px-4 sm:px-6 flex items-center justify-between flex-shrink-0 z-10 sticky top-0">
          <div className="min-w-0 flex items-center gap-2">
            <button className="taxhive-mobile-navigation-toggle p-1" aria-label="Toggle navigation"
              aria-expanded={mobileNavigationOpen} aria-controls="authenticated-navigation"
              onClick={() => setMobileNavigationOpen(open => !open)}>
              <Menu className="w-5 h-5" />
            </button>
            <h1 className="text-lg font-semibold text-text-primary truncate tracking-tight">
              {pageTitle}
            </h1>
          </div>

          <div className="flex items-center gap-1.5">
            <button
              onClick={() =>
                navigate(user.authScope === 'client_vault' ? '/client-vault' : '/search')
              }
              className="flex items-center gap-2 px-3 h-9 rounded-lg text-sm text-text-secondary border border-surface-overlay hover:bg-surface-overlay hover:text-text-primary transition"
              title="Search"
            >
              <Search className="w-4 h-4" />
              <span className="hidden md:inline">Search…</span>
            </button>

            {/* Accent theme switcher */}
            <div className="relative" ref={accentRef}>
              <button
                onClick={() => setAccentOpen(o => !o)}
                className="w-9 h-9 rounded-lg flex items-center justify-center text-text-secondary hover:bg-surface-overlay hover:text-text-primary transition"
                title="Accent colour"
                aria-label="Change accent colour"
                aria-haspopup="menu"
                aria-expanded={accentOpen}
              >
                <Palette className="w-5 h-5" />
              </button>
              {accentOpen && (
                <div
                  role="menu"
                  className="absolute right-0 mt-2 w-44 bg-white dark:bg-surface-elevated rounded-xl shadow-xl border border-surface-overlay overflow-hidden p-1 animate-in fade-in slide-in-from-top-2 duration-150"
                >
                  <p className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-text-tertiary">
                    Accent
                  </p>
                  {ACCENTS.map(a => (
                    <button
                      key={a.id}
                      role="menuitemradio"
                      aria-checked={accent === a.id}
                      onClick={() => pickAccent(a.id)}
                      className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-text-secondary hover:bg-surface-overlay hover:text-text-primary transition"
                    >
                      <span
                        className="w-4 h-4 rounded-full ring-1 ring-black/10 dark:ring-white/15 flex-shrink-0"
                        style={{ backgroundColor: a.swatch }}
                      />
                      <span className="flex-1 text-left">{a.label}</span>
                      {accent === a.id && <Check className="w-4 h-4 text-primary-600" />}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={toggleTheme}
              className="w-9 h-9 rounded-lg flex items-center justify-center text-text-secondary hover:bg-surface-overlay hover:text-text-primary transition"
              title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>

            <div className="w-px h-6 bg-surface-overlay mx-1" />

            {/* User menu */}
            <div className="relative" ref={menuRef}>
              <button
                aria-label="Account menu"
                onClick={() => setMenuOpen(o => !o)}
                className="flex items-center gap-2 pl-1 pr-2 h-9 rounded-lg hover:bg-surface-overlay transition"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
              >
                <span className="w-7 h-7 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 flex items-center justify-center text-xs font-semibold uppercase">
                  {initials(user.fullName)}
                </span>
                <span className="hidden sm:block text-sm font-medium text-text-primary max-w-[10rem] truncate">
                  {user.firstName || user.fullName}
                </span>
              </button>

              {menuOpen && (
                <div
                  role="menu"
                  className="absolute right-0 mt-2 w-56 bg-white dark:bg-surface-elevated rounded-xl shadow-xl border border-surface-overlay overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150"
                >
                  <div className="px-4 py-3 border-b border-surface-overlay">
                    <p className="text-sm font-semibold text-text-primary truncate">
                      {user.fullName}
                    </p>
                    <p className="text-xs text-text-tertiary truncate">{user.email}</p>
                  </div>
                  <button
                    role="menuitem"
                    onClick={() => {
                      setMenuOpen(false)
                      navigate(user.authScope === 'client_vault' ? '/client-vault' : '/profile')
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-text-secondary hover:bg-surface-overlay hover:text-text-primary transition"
                  >
                    <User className="w-4 h-4" /> Profile
                  </button>
                  <button
                    role="menuitem"
                    onClick={() => {
                      setMenuOpen(false)
                      navigate(user.authScope === 'client_vault' ? '/client-vault' : '/settings')
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-text-secondary hover:bg-surface-overlay hover:text-text-primary transition"
                  >
                    <Settings className="w-4 h-4" /> Settings
                  </button>
                  <button
                    role="menuitem"
                    onClick={() => {
                      setMenuOpen(false)
                      logout()
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 border-t border-surface-overlay transition"
                  >
                    <LogOut className="w-4 h-4" /> Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto bg-surface-light dark:bg-surface-dark">
          <div className="max-w-7xl mx-auto p-4 sm:p-6 w-full">
            {user.authScope !== 'client_vault' && <TenantSupportNotice />}
            <Outlet />
          </div>
        </main>
      </div>

      <ToastContainer />
    </div>
  )
}
